import { notification } from "antd"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query"
import {
  addEditConsultation,
  ConsultSaveRequest,
  createJob,
  CreateJobRequest,
  CreateJobType,
  CustomerSaveRequest,
  deleteCustomer,
  downloadConsultationIcs,
  EmailAddresses,
  filterConsultationCannedResponses,
  getCannedResponses,
  getConsultationProjects,
  getCustomer,
  getCustomerDetail,
  getCustomerInvoices,
  getCustomerPhotos,
  getCustomerProperties,
  getCustomers,
  getCustomerTags,
  getSalesLeads,
  getSalesUsers,
  importQboCustomers,
  restoreCustomer,
  saveCustomer,
  sendConsultationInvites,
} from "./customerApi"
import { getCustomerVisits } from "./customerVisitsApi"
import { qboImportErrorMessage } from "./importQboCustomersModel"
import { QboCustomerRecord } from "../../domain/quickbooks/quickbooksModel"
import { useQboCustomersInvalidator } from "../../domain/quickbooks/quickbooksQueries"
import { saveIcs } from "../../services/fileSystemService"

export const useCustomerQuery = (customerId: number) => {
  return useQuery(['customer', customerId], () => getCustomer(customerId), {
    enabled: !!customerId,
    refetchOnWindowFocus: false
  })
}

export const useCustomersQuery = (searchQuery: string, pageSize: number, isLead: boolean, isCustomer: boolean, updateTrigger: number, showDeleted: boolean) => {
  return useInfiniteQuery(
    ['customers', { searchQuery, pageSize, isLead, isCustomer, showDeleted }, updateTrigger],
    ({ pageParam = 1 }) => getCustomers(searchQuery, pageSize, pageParam, isLead, isCustomer, showDeleted),
    {
      getNextPageParam: (_, pages) => pages.length + 1,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({
          message: 'There was a problem loading the customers. Please try again after a few moments.'
        })
      }
    }
  )
}

export const useInvalidateCustomers = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(['customers'])
}

export const useCustomerDetailQuery = (customerId: number | null | undefined, showDeleted = false) => {
  return useQuery(
    ['customer-detail', customerId, showDeleted],
    () => getCustomerDetail(customerId!, showDeleted),
    {
      enabled: !!customerId,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({ message: 'There was an error loading the customer.' })
      }
    }
  )
}

export const useCustomerDetailInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateCustomer: (customerId: number) => queryClient.invalidateQueries(['customer-detail', customerId]),
    invalidateAllCustomerDetails: () => queryClient.invalidateQueries(['customer-detail']),
  }
}

export const useSalesUsersQuery = () => {
  return useQuery(['sales-users'], getSalesUsers, {
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'There was an error getting the list of sales users' })
    }
  })
}

export const useSalesLeadsQuery = () => {
  return useQuery(['sales-leads'], getSalesLeads, {
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'There was an error getting the list of sales leads' })
    }
  })
}

// Feedback lives here, not at the call site, so it still shows if the page
// unmounts before a slow import settles.
export const useImportQboCustomersMutation = () => {
  const queryClient = useQueryClient()
  const { invalidateQboCustomers } = useQboCustomersInvalidator()
  return useMutation((customers: QboCustomerRecord[]) => importQboCustomers(customers), {
    onSuccess: summary => {
      const message = summary?.trim() || 'Customers imported from QuickBooks'
      window.Intercom?.('trackEvent', 'imported-customers', { message, success: true })
      notification.success({ message })
    },
    onError: error => {
      const message = qboImportErrorMessage(error, 'There was an error importing customers from QuickBooks')
      window.Intercom?.('trackEvent', 'imported-customers', { message, success: false })
      notification.error({ message: 'Import from QuickBooks', description: message, style: { whiteSpace: 'pre-line' } })
    },
    onSettled: () => {
      queryClient.invalidateQueries(['customers'])
      invalidateQboCustomers()
    },
  })
}

export const useSalesLeadsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateSalesLeads: () => queryClient.invalidateQueries(['sales-leads']),
  }
}

export const useCannedResponsesQuery = () => {
  return useQuery(['canned-responses'], getCannedResponses, {
    refetchOnWindowFocus: false,
    select: filterConsultationCannedResponses,
    onError: () => {
      notification.error({ message: 'There was an error getting the list of canned responses' })
    }
  })
}

export const useConsultationProjectsQuery = (customerId: number | null | undefined, enabled = true) => {
  return useQuery(
    ['consultation-projects', customerId],
    () => getConsultationProjects(customerId!),
    {
      enabled: !!customerId && enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export const useCustomerTagsQuery = (search: string, enabled = true) => {
  return useQuery(['customer-tags', search], () => getCustomerTags(search), {
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}

export const useCustomerPhotosQuery = (customerId: number | null | undefined, enabled = true) => {
  return useQuery(
    ['customer-photos', customerId],
    () => getCustomerPhotos(customerId!),
    {
      enabled: !!customerId && enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export const useCustomerPropertiesQuery = (customerId: number | null | undefined, enabled = true) => {
  return useQuery(
    ['customer-properties', customerId],
    () => getCustomerProperties(customerId!),
    {
      enabled: !!customerId && enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export const useCustomerInvoicesQuery = (customerId: number | null | undefined, enabled = true) => {
  return useQuery(
    ['customer-invoices', customerId],
    () => getCustomerInvoices(customerId!),
    {
      enabled: !!customerId && enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export const useSaveCustomerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ customer, createFirstPropertyAutomatically }: { customer: CustomerSaveRequest; createFirstPropertyAutomatically?: boolean }) =>
      saveCustomer(customer, createFirstPropertyAutomatically),
    {
      onSuccess: (saved) => {
        queryClient.invalidateQueries(['customers'])
        queryClient.invalidateQueries(['customer-detail', saved.id])
      },
      onError: () => {
        notification.error({ message: 'There was an error saving this customer' })
      }
    }
  )
}

export const useDeleteCustomerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((customerId: number) => deleteCustomer(customerId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['customers'])
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message ?? 'There was an error removing this customer.'
      notification.error({ message })
    }
  })
}

export const useRestoreCustomerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((customerId: number) => restoreCustomer(customerId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['customers'])
    },
    onError: () => {
      notification.error({ message: 'There was an error restoring this customer' })
    }
  })
}

export const useAddEditConsultationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((consult: ConsultSaveRequest) => addEditConsultation(consult), {
    onSuccess: (_, consult) => {
      if (consult.customerId) {
        queryClient.invalidateQueries(['customer-detail', consult.customerId])
      } else {
        queryClient.invalidateQueries(['customer-detail'])
      }
    },
    onError: () => {
      notification.error({ message: 'There was an error saving the consultation' })
    }
  })
}

export const useSendConsultationInvitesMutation = () => {
  return useMutation(
    ({ customerId, consultId, emailAddresses }: { customerId: number; consultId: number; emailAddresses: EmailAddresses }) =>
      sendConsultationInvites(customerId, consultId, emailAddresses),
    {
      onError: () => {
        notification.error({ message: 'There was an error sending your invites. Please try again later.' })
      }
    }
  )
}

export const useCreateJobMutation = () => {
  return useMutation(({ type, request }: { type: CreateJobType; request: CreateJobRequest }) => createJob(type, request), {
    onError: () => {
      notification.error({ message: 'There was an error creating this project' })
    }
  })
}

export const useDownloadConsultationIcsMutation = () => {
  return useMutation((consultId: number) => downloadConsultationIcs(consultId), {
    onSuccess: (blob) => {
      saveIcs(blob, 'calendar.ics')
    },
    onError: () => {
      notification.error({ message: 'There was an error downloading the calendar invite.' })
    }
  })
}

export const useCustomerVisitsQuery = (customerId: number, page: number, pageSize: number) => {
  return useQuery(
    ['customer-visits', customerId, page, pageSize],
    () => getCustomerVisits(customerId, page, pageSize),
    {
      enabled: !!customerId,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({ message: 'There was an error loading the visits for this customer.' })
      }
    }
  )
}
